import { useCallback, useMemo, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { createSegment } from "src/services/SegmentAPIs";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import { AUDIENCE_ROUTE } from "../CreateList/createList.constants";
import { useAudienceTags } from "../Shared/UseAudienceTags";
import { CREATE_SEGMENT_STEPS } from "./CreateSegment.Constants";
import { areRulesComplete, buildSegmentConditions } from "./CreateSegment.Utils";
import { useSegmentConditions } from "./UseSegmentConditions";
import { useSegmentLabel } from "./UseSegmentLabel";
import { useSegmentPreview } from "./UseSegmentPreview";
import { useSegmentPrompt } from "./UseSegmentPrompt";
import { useSegmentSummary } from "./UseSegmentSummary";

export const useCreateSegment = () => {
  const navigate = useNavigate();
  const label = useSegmentLabel();

  const [stepIndex, setStepIndex] = useState(0);
  const [segmentName, setSegmentName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdCount, setCreatedCount] = useState<number | null>(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const tagsState = useAudienceTags();
  const conditionsState = useSegmentConditions();
  const { rules } = conditionsState;
  const prompt = useSegmentPrompt({
    rules,
    onFill: conditionsState.fillConditions,
    onRestore: conditionsState.restoreRules,
  });

  const stepKey = CREATE_SEGMENT_STEPS[stepIndex].key;
  const rulesComplete = useMemo(() => areRulesComplete(rules), [rules]);
  const summary = useSegmentSummary(rules, rulesComplete);

  const { preview, status: previewStatus } = useSegmentPreview(rules, rulesComplete);

  const canProceed = {
    rules: rulesComplete && previewStatus === "ready",
    details: Boolean(segmentName.trim()),
    review: !creating,
  }[stepKey];

  const createSegmentRequest = async () => {
    setCreating(true);
    try {
      const response = await createSegment({
        segment_name: segmentName.trim(),
        description: description.trim() || undefined,
        tags: tagsState.tags,
        conditions: buildSegmentConditions(rules),
      });
      setCreatedCount(response.contact_count);
    } catch (error) {
      message.error(getThrownApiDetail(error) || label("errors.createFailed"));
    } finally {
      setCreating(false);
    }
  };

  const goNext = async () => {
    if (stepKey === "review") {
      await createSegmentRequest();
      return;
    }
    setShowFieldErrors(false);
    setStepIndex(index => Math.min(index + 1, CREATE_SEGMENT_STEPS.length - 1));
  };

  const handleBlockedNext = useCallback(() => {
    setShowFieldErrors(true);
    if (stepKey === "rules") {
      message.error(
        label(rulesComplete && previewStatus === "error" ? "errors.previewFailed" : "errors.rulesIncomplete")
      );
    }
  }, [stepKey, label, rulesComplete, previewStatus]);

  const goBack = () => {
    if (stepIndex === 0) {
      navigate(AUDIENCE_ROUTE);
      return;
    }
    setShowFieldErrors(false);
    setStepIndex(index => index - 1);
  };

  return {
    ...conditionsState,
    ...tagsState,
    prompt,
    stepIndex,
    stepKey,
    segmentName,
    description,
    creating,
    createdCount,
    showFieldErrors,
    canProceed,
    preview,
    previewStatus,
    summary,
    setSegmentName,
    setDescription,
    goNext,
    goBack,
    handleBlockedNext,
  };
};
